// Section detector — long-horizon classifier over spring values.
// Classifies into: silence, dense, bass-heavy, mids-heavy, treble-heavy.
// Fire requires candidate ≠ current AND short/long deviation AND cooldown.
export class SectionDetector {
  constructor({
    shortLen = 120,
    longLen = 1800,
    silenceThreshold = 0.02,
    denseThreshold = 0.6,
    dominanceMargin = 0.10,
    dominanceFloor = 0.15,
    deviationThreshold = 0.30,
    cooldownFrames = 1200,
  } = {}) {
    this.shortLen = shortLen;
    this.longLen = longLen;
    this.silenceThreshold = silenceThreshold;
    this.denseThreshold = denseThreshold;
    this.dominanceMargin = dominanceMargin;
    this.dominanceFloor = dominanceFloor;
    this.deviationThreshold = deviationThreshold;
    this.cooldownFrames = cooldownFrames;

    this.shortBuf = new Float32Array(shortLen);
    this.longBuf = new Float32Array(longLen);
    this.shortIdx = 0;
    this.longIdx = 0;
    this.shortFill = 0;
    this.longFill = 0;

    this.section = 'silence';
    this.sectionId = 0;
    this.sectionChangedAt = 0;
    this.lastChangeFrame = -Infinity;
    this.frameCount = 0;
  }

  update(bass, mids, treble, nowMs) {
    const rms = Math.sqrt((bass * bass + mids * mids + treble * treble) / 3);

    this.shortBuf[this.shortIdx] = rms;
    this.shortIdx = (this.shortIdx + 1) % this.shortLen;
    if (this.shortFill < this.shortLen) this.shortFill++;
    this.longBuf[this.longIdx] = rms;
    this.longIdx = (this.longIdx + 1) % this.longLen;
    if (this.longFill < this.longLen) this.longFill++;

    let sSum = 0;
    for (let i = 0; i < this.shortFill; i++) sSum += this.shortBuf[i];
    const shortMean = sSum / this.shortFill;
    let lSum = 0;
    for (let i = 0; i < this.longFill; i++) lSum += this.longBuf[i];
    const longMean = lSum / this.longFill;

    let candidate = this.section;
    if (shortMean < this.silenceThreshold) {
      candidate = 'silence';
    } else if (bass >= this.denseThreshold && mids >= this.denseThreshold && treble >= this.denseThreshold) {
      candidate = 'dense';
    } else {
      const bands = [
        { name: 'bass', v: bass },
        { name: 'mids', v: mids },
        { name: 'treble', v: treble },
      ];
      bands.sort((a, b) => b.v - a.v);
      if (bands[0].v - bands[1].v >= this.dominanceMargin && bands[0].v >= this.dominanceFloor) {
        candidate = `${bands[0].name}-heavy`;
      }
      // else: retain current (no change to `candidate`)
    }

    const deviation = Math.abs(shortMean - longMean) / Math.max(longMean, 0.05);
    const cooldownOk = this.frameCount - this.lastChangeFrame >= this.cooldownFrames;
    let changed = false;
    if (candidate !== this.section && deviation > this.deviationThreshold && cooldownOk) {
      this.section = candidate;
      this.sectionId++;
      this.sectionChangedAt = nowMs;
      this.lastChangeFrame = this.frameCount;
      changed = true;
    }
    this.frameCount++;
    return {
      section: this.section,
      sectionId: this.sectionId,
      sectionChangedAt: this.sectionChangedAt,
      changed,
    };
  }
}
